// WorkspaceContext — Stage 2 scaffolding.
//
// Two-context pattern:
//   WorkspaceContext       — read context. Consumers re-render on changes.
//   WorkspaceContextSetter — writer context. Stable callback; writers don't
//                            re-render on changes.
//
// The provider holds an internal tab_state object keyed by tab name. Each
// tab publishes its slice via publish(tabKey, partial). The provider
// merges partials and exposes the full WorkspaceContext value derived
// from the tab_state plus its App-level inputs (device, simSettings,
// activeTab, latestSimulation, theme).
//
// Per design doc §3.2, §3.4, §3.5.

import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';

export const WorkspaceContext = createContext(null);
export const WorkspaceContextSetter = createContext(() => {});

// All tab slots start as `context_pending` per design resolution B —
// distinguishes "tab exists but hasn't reported yet" from "tab doesn't
// exist." device_modal is null when closed; populated only while open.
const PENDING = { status: 'context_pending' };
const EMPTY_TAB_STATE = {
  simulation:   { ...PENDING },
  profiles:     { ...PENDING },
  curva:        { ...PENDING },
  avarodha:     { ...PENDING },
  pl_trpl:      { ...PENDING },
  eqe:          { ...PENDING },
  cv:           { ...PENDING },
  pl_analysis:  { ...PENDING },
  parameter_library: { ...PENDING },
  feedback:     { ...PENDING },
  device_modal: null,
};

// App.js uses 'pl' for PL/TRPL and 'pl-analysis' for PL Analysis in its
// internal activeTab state. Context normalizes to 'pl_trpl' / 'pl_analysis'
// so the backend-facing keys match the design's `active_tab` enum.
function normalizeTab(t) {
  if (t === 'pl') return 'pl_trpl';
  if (t === 'pl-analysis') return 'pl_analysis';
  return t;
}

// ── Chat UI state lookup ─────────────────────────────────────────────────
// PersistentAI persists width and collapsed to localStorage and dispatches
// `jvista-chat-ui-change` after each write. We listen for that event so the
// provider's `ui` slice stays in sync with what the chat is doing.
const CHAT_WIDTH_KEY = 'jvista_chat_width_v1';
const CHAT_COLLAPSED_KEY = 'jvista_chat_collapsed_v1';

function readChatUiFromStorage() {
  const widthRaw = parseInt(localStorage.getItem(CHAT_WIDTH_KEY) || '', 10);
  const width = Number.isFinite(widthRaw) ? widthRaw : 400;
  const collapsed = localStorage.getItem(CHAT_COLLAPSED_KEY) === 'true';
  return { width, collapsed };
}

// Shallow-compare helper used by publish() to avoid no-op state churn.
function shallowEqualSubset(prev, partial) {
  if (!prev) return false;
  for (const k of Object.keys(partial)) {
    if (prev[k] !== partial[k]) return false;
  }
  return true;
}

export function WorkspaceContextProvider({
  children,
  activeTab,
  device,
  simSettings,
  latestSimulation,
  theme,
}) {
  const [tabState, setTabState] = useState(EMPTY_TAB_STATE);

  // ── publish: merge a partial slice into tab_state[tabKey]. ──────────
  // Called by tab components from useEffect. Stable identity (no deps)
  // so consumers' deps arrays don't churn.
  const publish = useCallback((tabKey, partial) => {
    if (!tabKey) return;
    setTabState(prev => {
      // device_modal supports an explicit null to clear (modal closed).
      if (tabKey === 'device_modal' && partial === null) {
        if (prev.device_modal === null) return prev;
        return { ...prev, device_modal: null };
      }
      const cur = prev[tabKey];
      // Already-equal short-circuit — keeps the value stable so memoized
      // consumers don't churn.
      if (
        cur &&
        shallowEqualSubset(cur, partial) &&
        Object.keys(cur).length === Object.keys(partial).length
      ) {
        return prev;
      }
      return { ...prev, [tabKey]: { ...(cur || {}), ...partial } };
    });
  }, []);

  // ── simulation_history: capped to 10 most-recent runs ───────────────
  // Append when a fresh `latestSimulation` arrives (reference-equal check
  // so we don't double-push when other state changes re-render us).
  const [simulationHistory, setSimulationHistory] = useState([]);
  const lastSimRef = useRef(null);
  useEffect(() => {
    if (!latestSimulation || latestSimulation === lastSimRef.current) return;
    lastSimRef.current = latestSimulation;
    if (!latestSimulation.metrics) return;
    const m = latestSimulation.metrics;
    const entry = {
      id: latestSimulation.job_id || `sim_${Date.now()}`,
      timestamp: new Date().toISOString(),
      stack_summary: tabState.simulation?.stack_summary || '',
      metrics: { Voc: m.Voc, Jsc: m.Jsc, FF: m.FF, PCE: m.PCE },
    };
    setSimulationHistory(prev => [entry, ...prev].slice(0, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestSimulation]);

  // ── Chat UI state subscription ──────────────────────────────────────
  const [chatUi, setChatUi] = useState(readChatUiFromStorage);
  useEffect(() => {
    function onChange() { setChatUi(readChatUiFromStorage()); }
    window.addEventListener('jvista-chat-ui-change', onChange);
    // Cross-window storage event too (multi-tab safety; cheap).
    function onStorage(e) {
      if (e.key === CHAT_WIDTH_KEY || e.key === CHAT_COLLAPSED_KEY) onChange();
    }
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('jvista-chat-ui-change', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // ── Effective active_tab: device_modal overrides the underlying tab.
  const effectiveActiveTab = useMemo(() => {
    if (tabState.device_modal?.open) return 'device_modal';
    return normalizeTab(activeTab);
  }, [tabState.device_modal, activeTab]);

  // ── The exposed value ───────────────────────────────────────────────
  const value = useMemo(() => ({
    active_tab: effectiveActiveTab,
    device_spec: device,
    sim_settings: simSettings,
    latest_simulation: latestSimulation,
    simulation_history: simulationHistory,
    tab_state: tabState,
    ui: {
      theme,
      chat_collapsed: chatUi.collapsed,
      chat_width_px: chatUi.width,
    },
  }), [
    effectiveActiveTab, device, simSettings, latestSimulation,
    simulationHistory, tabState, theme, chatUi.collapsed, chatUi.width,
  ]);

  return (
    <WorkspaceContext.Provider value={value}>
      <WorkspaceContextSetter.Provider value={publish}>
        {children}
      </WorkspaceContextSetter.Provider>
    </WorkspaceContext.Provider>
  );
}
